
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';
import { broadcast, onSessionMessage, SESSION_MSG } from '../lib/sessionChannel';
import { usePermessiStore } from './permessi.store';
import { azzeraDatiDiSessione } from '../lib/datiDiSessione';
import { sessioneUtilizzabile, impersonazioneOrfana, normalizzaUtente } from '../lib/sessione';

// Single source of truth for session state. Token reads from api.js, token
// writes from auth flows, the SessionGuard countdown and the TopBar timer
// badge all consume THIS store directly via getState() / hooks. No tactical
// caches anywhere else — that was the bug that caused tokens to drift across
// layers and made "Sessione persa" appear during a perfectly healthy session.
//
// Two timestamps drive the freshness/UX logic:
//   - loginAt: ms epoch at which the current SSO session started. Reset on
//     every successful login + cleared on logout. Drives the "Connesso da Xm"
//     badge and the freshness gate that suppresses spurious 401s in the
//     first 30 s after login.
//   - lastRefreshAt: ms epoch of the most recent SUCCESSFUL token rotation.
//     Drives the same 30-s freshness gate post-refresh so that a 401 from
//     downstream data services right after a rotation isn't misread as a
//     session expiry.
// Tutto quello che tiene in piedi un'impersonazione. Si azzera insieme, e in
// tre punti: entrando, uscendo, e quando è un'altra finestra a chiudere la
// sessione. Un pezzo lasciato indietro significa una fascia rossa che parla
// di una sessione che non c'è più, e un pulsante «Termina impersonazione»
// che rimette in mano token scaduti.
const IMPERSONAZIONE_AZZERATA = {
  impersonating: null,
  _originalUser: null,
  _originalAccessToken: null,
  _originalRefreshToken: null,
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      impersonating: null,
      _originalUser: null,
      _originalAccessToken: null,
      _originalRefreshToken: null,
      loginAt: 0,
      lastRefreshAt: 0,
      // Screen lock. Orthogonal to isAuthenticated ON PURPOSE: the session is
      // still valid and the app stays mounted — which is what keeps the
      // softphone registered and able to take calls — while an overlay hides
      // the data. Logging out instead would unmount AppShell, stop the SIP
      // user agent and silently drop incoming calls, which is the behaviour
      // this replaces.
      locked: false,

      setTokens: (accessToken, refreshToken) => {
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true,
          lastRefreshAt: Date.now(),
        });
      },

      login: async ({ user, accessToken, refreshToken }) => {
        const now = Date.now();
        // Anche entrare è un cambio di identità: nella stessa scheda può
        // essersi collegata prima un'altra persona, e i suoi elenchi sono
        // ancora in memoria.
        azzeraDatiDiSessione();
        set({
          user: normalizzaUtente(user),
          accessToken,
          refreshToken,
          isAuthenticated: true,
          error: null,
          loginAt: now,
          lastRefreshAt: now,
          locked: false,
          // I resti di un'impersonazione appartengono alla sessione appena
          // finita. Restavano in memoria (sono persistiti) e la sessione nuova
          // se li ritrovava addosso: la fascia rossa diceva «stai
          // visualizzando come…» di una sessione che non esisteva più, e
          // «Termina impersonazione» rimetteva in mano token morti.
          ...IMPERSONAZIONE_AZZERATA,
        });
      },

      // Inactivity reached zero. Hide the data; keep the session and the
      // phone. Broadcast so every window locks together — locking one and
      // leaving another open would defeat the point.
      lock: () => {
        if (!get().isAuthenticated || get().locked) return;
        set({ locked: true });
        broadcast(SESSION_MSG.lock, {});
      },

      // Re-authenticated in place from the lock overlay. The auth endpoints
      // mint a fresh session, so the new pair replaces the old one here and
      // in the other windows.
      unlock: ({ user, accessToken, refreshToken }) => {
        const now = Date.now();
        const nextUser = user ? normalizzaUtente(user, get().user) : undefined;
        set({
          ...(nextUser ? { user: nextUser } : {}),
          ...(accessToken ? { accessToken, refreshToken: refreshToken || get().refreshToken } : {}),
          locked: false,
          error: null,
          lastRefreshAt: now,
        });
        broadcast(SESSION_MSG.unlock, {});
        if (accessToken) broadcast(SESSION_MSG.tokens, { accessToken, refreshToken });
      },

      logout: async () => {
        const { accessToken, refreshToken } = get();
        if (accessToken) {
          try {
            // The server identifies the session to revoke by the refresh token
            // we hold: the access token carries no session id. Until this was
            // sent, the call hit /sessions/:id with the literal word
            // "current", failed, and left the session alive on the server.
            await api.delete('/auth/sessions/current', { body: JSON.stringify({ refreshToken }) });
          } catch (err) {
            // The local state is cleared either way — refusing to log out
            // because the server did not answer would trap the user. But this
            // is no longer swallowed in silence: a failure here means the
            // session is still valid elsewhere, and that is worth saying.
            console.error('[auth] revoca della sessione non riuscita, la sessione resta attiva sul server:', err?.message || err);
          }
        }
        // One session, one outcome: revoking it here must not leave another
        // window believing it is still signed in and refreshing a dead token.
        broadcast(SESSION_MSG.logout, {});
        // I permessi appartengono alla sessione che sta finendo: lasciarli in
        // memoria significa che il prossimo che entra da questo browser parte
        // con l'insieme di qualcun altro finché la lettura non ritorna.
        azzeraDatiDiSessione();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          loginAt: 0,
          lastRefreshAt: 0,
          locked: false,
          ...IMPERSONAZIONE_AZZERATA,
        });
      },

      // Called from api.js after a successful /auth/refresh — never from
      // page code. The rotated pair lands in the store and the lastRefreshAt
      // stamp is bumped in the same atomic set() so the freshness gate is
      // immediately consistent with the persisted tokens.
      // Adopt a pair another window just rotated. Deliberately NOT
      // re-broadcast: the sender already told everyone, and echoing would
      // bounce the pair around the bus.
      _adoptRemoteTokens: (accessToken, refreshToken) => {
        if (!accessToken) return;
        if (!get().isAuthenticated) return;   // signed out here — ignore
        set({ accessToken, refreshToken: refreshToken || get().refreshToken, lastRefreshAt: Date.now() });
      },

      // Another window ended the session. Clear local state without calling
      // the revoke endpoint again — it is already revoked server-side.
      _clearAfterRemoteLogout: () => {
        azzeraDatiDiSessione();
        set({
          user: null, accessToken: null, refreshToken: null,
          isAuthenticated: false, error: null, loginAt: 0, lastRefreshAt: 0, locked: false,
          ...IMPERSONAZIONE_AZZERATA,
        });
      },

      _ingestRotatedTokens: (accessToken, refreshToken) => {
        set({
          accessToken,
          refreshToken: refreshToken || get().refreshToken,
          lastRefreshAt: Date.now(),
        });
        queueMicrotask(() => {
          if (!get().isAuthenticated) return;
          get().fetchUser?.({ silent: true })?.catch?.(() => {});
          usePermessiStore.getState().carica?.({ silenzioso: true })?.catch?.(() => {});
        });
      },

      refresh: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          get().logout();
          return;
        }
        try {
          const resp = await api.post('/auth/refresh', { refreshToken });
          get()._ingestRotatedTokens(resp.data.accessToken, resp.data.refreshToken);
        } catch (_) {
          get().logout();
        }
      },

      fetchUser: async ({ silent = false } = {}) => {
        try {
          if (!silent) set({ isLoading: true });
          const resp = await api.get('/users/me');
          set({
            user: normalizzaUtente(resp.data, get().user),
            ...(silent ? {} : { isLoading: false }),
          });
        } catch (err) {
          if (!silent) set({ isLoading: false, error: err.message });
        }
      },

      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      startImpersonation: async (userId) => {
        const { user, accessToken, refreshToken } = get();
        try {
          const resp = await api.post('/auth/impersonate', { userId });
          const data = resp.data;
          const targetUser = normalizzaUtente(data.user);
          // Da qui in avanti le richieste partono con l'identità di un'altra
          // persona: tutto quello che è in memoria appartiene a chi
          // impersona, e va buttato via prima che le pagine lo riusino.
          // Senza questa riga si impersonava un ADMIN e si continuavano a
          // vedere le 38 persone caricate da SUPER_ADMIN.
          azzeraDatiDiSessione();
          set({
            _originalUser: user,
            _originalAccessToken: accessToken,
            _originalRefreshToken: refreshToken,
            impersonating: targetUser,
            user: targetUser,
            accessToken: data.accessToken,
            lastRefreshAt: Date.now(),
          });
          broadcast(SESSION_MSG.impersonation, {
            active: true,
            user: targetUser,
            accessToken: data.accessToken,
            originalUser: user,
            originalAccessToken: accessToken,
            originalRefreshToken: refreshToken,
          });
          usePermessiStore.getState().carica?.()?.catch?.(() => {});
        } catch (err) {
          set({ error: err.message || 'Errore durante l\'impersonazione' });
        }
      },

      // Torna alla propria identità. Restituisce `true` se la sessione
      // ripristinata è ancora utilizzabile: chi chiama (api.js, quando il
      // token di impersonazione scade da solo) deve poter distinguere «sei
      // tornato al tuo posto» da «era finita anche la tua sessione».
      stopImpersonation: () => {
        const { _originalUser, _originalAccessToken, _originalRefreshToken, impersonating } = get();
        if (!impersonating && !_originalAccessToken) return false;
        // Vale nei due sensi: tornando in sé non si devono riusare gli
        // elenchi ristretti della persona impersonata.
        azzeraDatiDiSessione();
        set({
          user: _originalUser,
          accessToken: _originalAccessToken,
          refreshToken: _originalRefreshToken,
          ...IMPERSONAZIONE_AZZERATA,
          lastRefreshAt: Date.now(),
        });
        broadcast(SESSION_MSG.impersonation, {
          active: false,
          user: _originalUser,
          accessToken: _originalAccessToken,
          refreshToken: _originalRefreshToken,
        });
        const usable = sessioneUtilizzabile({
          isAuthenticated: true,
          accessToken: _originalAccessToken,
          refreshToken: _originalRefreshToken,
        });
        if (usable) {
          usePermessiStore.getState().carica?.()?.catch?.(() => {});
        }
        return usable;
      },
    }),
    {
      name: 'wfm-auth',
      // Un'impersonazione orfana non sopravvive a un ricaricamento.
      //
      // Lo stato è persistito, quindi il residuo di un'impersonazione finita
      // male restava lì per giorni: la fascia rossa dichiarava un'identità
      // diversa da quella con cui le richieste partivano davvero, e «Termina
      // impersonazione» rimetteva in mano una coppia di token morti.
      //
      // Si ripulisce **qui** e non in `onRehydrateStorage` per un motivo
      // preciso, verificato in produzione: con localStorage la reidratazione
      // è sincrona e avviene *dentro* `create()`, quando la costante
      // `useAuthStore` non esiste ancora — quindi da lì non si può chiamare
      // `useAuthStore.setState`, e infatti non succedeva niente. `merge` vede
      // lo stato prima che venga applicato e non dipende da nessuna variabile
      // esterna.
      merge: (persistito, corrente) => {
        const unito = { ...corrente, ...(persistito || {}) };
        if (impersonazioneOrfana(unito)) {
          unito.impersonating = null;
          unito._originalUser = null;
          unito._originalAccessToken = null;
          unito._originalRefreshToken = null;
        }
        return unito;
      },
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        impersonating: state.impersonating,
        _originalUser: state._originalUser,
        _originalAccessToken: state._originalAccessToken,
        _originalRefreshToken: state._originalRefreshToken,
        loginAt: state.loginAt,
        lastRefreshAt: state.lastRefreshAt,
        // Persisted so reloading the page cannot be used to walk past the
        // lock screen.
        locked: state.locked,
      })
    }
  )
);

// Wire this window to the shared session bus. Module scope so it is live for
// the whole lifetime of the app, and guarded for non-browser environments
// (tests, SSR) where BroadcastChannel does not exist.
if (typeof window !== 'undefined') {
  onSessionMessage(SESSION_MSG.tokens, ({ accessToken, refreshToken }) => {
    useAuthStore.getState()._adoptRemoteTokens(accessToken, refreshToken);
  });
  onSessionMessage(SESSION_MSG.logout, () => {
    useAuthStore.getState()._clearAfterRemoteLogout();
  });
  onSessionMessage(SESSION_MSG.lock, () => {
    const st = useAuthStore.getState();
    if (st.isAuthenticated && !st.locked) useAuthStore.setState({ locked: true });
  });
  onSessionMessage(SESSION_MSG.unlock, () => {
    if (useAuthStore.getState().locked) useAuthStore.setState({ locked: false });
  });
  onSessionMessage(SESSION_MSG.impersonation, (msg) => {
    const st = useAuthStore.getState();
    if (!st.isAuthenticated) return;
    azzeraDatiDiSessione();
    if (msg?.active && msg.user && msg.accessToken) {
      useAuthStore.setState({
        _originalUser: msg.originalUser || st._originalUser || st.user,
        _originalAccessToken: msg.originalAccessToken || st._originalAccessToken || st.accessToken,
        _originalRefreshToken: msg.originalRefreshToken || st._originalRefreshToken || st.refreshToken,
        impersonating: msg.user,
        user: msg.user,
        accessToken: msg.accessToken,
        lastRefreshAt: Date.now(),
      });
    } else {
      useAuthStore.setState({
        user: msg?.user || st._originalUser || st.user,
        accessToken: msg?.accessToken || st._originalAccessToken || st.accessToken,
        refreshToken: msg?.refreshToken || st._originalRefreshToken || st.refreshToken,
        ...IMPERSONAZIONE_AZZERATA,
        lastRefreshAt: Date.now(),
      });
    }
    usePermessiStore.getState().carica?.()?.catch?.(() => {});
  });
}
